import React, { useEffect, useState } from "react";
import api, { money, mediaUrl } from "@/lib/api";
import { useLang, tr, loc } from "@/lib/i18n";

const groupItems = (products) => {
  const groups = [];
  products.forEach((p) => {
    const g = p.group || "";
    const last = groups[groups.length - 1];
    if (last && last.name === g) last.items.push(p);
    else groups.push({ name: g, items: [p] });
  });
  return groups;
};

const Dish = ({ p, lang }) => {
  const desc = loc(p, "description", lang);
  return (
    <div className="flex gap-4 py-4 border-b border-dashed border-gold/25" data-testid="public-menu-item">
      {p.image && <img src={mediaUrl(p.image)} alt={loc(p, "name", lang)} loading="lazy" className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3">
          <span className="text-forest-deep font-medium text-base sm:text-lg" data-testid="public-menu-item-name">{loc(p, "name", lang)}</span>
          <span className="flex-1 border-b border-dotted border-gold/40 translate-y-[-4px] min-w-[1rem]" />
          <span className="text-gold-dark font-semibold whitespace-nowrap" data-testid="public-menu-price">{money(p.price)}</span>
        </div>
        {(p.weight || desc) && (
          <div className="text-sm text-muted-foreground mt-1">
            {p.weight && <span className="text-forest/70 font-medium mr-2">{p.weight}</span>}{desc}
          </div>
        )}
      </div>
    </div>
  );
};

const CategoryBlock = ({ block, idx, lang }) => {
  const note = loc(block.category, "note", lang);
  return (
    <section id={`cat-${block.category.id}`} className="scroll-mt-40" data-testid="public-menu-category">
      <div className="flex items-end gap-4">
        <span className="font-display text-5xl text-gold/60 leading-none">{String(idx + 1).padStart(2, "0")}</span>
        <h3 className="font-display text-3xl sm:text-4xl text-forest-deep uppercase tracking-wide" data-testid="public-menu-category-name">{loc(block.category, "name", lang)}</h3>
      </div>
      <div className="gold-rule w-20 my-4" />
      {note && <p className="italic text-forest/80 mb-2" data-testid="public-menu-category-note">{note}</p>}
      {groupItems(block.products).map((g, i) => (
        <div key={i} className="mt-2">
          {g.name && <h4 className="text-sm uppercase tracking-[0.2em] text-gold-dark mt-6 mb-1">{loc(g.items[0], "group", lang)}</h4>}
          <div className="grid md:grid-cols-2 gap-x-12">{g.items.map((p) => <Dish key={p.id} p={p} lang={lang} />)}</div>
        </div>
      ))}
    </section>
  );
};

export const PublicMenu = ({ langOverride, menuOverride, compact }) => {
  const ctx = useLang();
  const lang = langOverride || ctx.lang;
  const [fetched, setFetched] = useState(null);
  useEffect(() => { if (!menuOverride) api.get("/public/menu").then((r) => setFetched(r.data)).catch(() => setFetched([])); }, [menuOverride]);
  const menu = menuOverride || fetched;
  const go = (id) => document.getElementById(`cat-${id}`)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className={`max-w-7xl mx-auto px-4 ${compact ? "py-6" : "py-16"}`} data-testid="public-menu-page">
      <p className="text-xs uppercase tracking-[0.3em] text-gold-dark">{tr(lang, "menu_kicker")}</p>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-forest-deep mt-2" data-testid="public-menu-title">{tr(lang, "menu_title")}</h2>
      <div className="gold-rule w-24 my-5" />
      {menu && menu.length > 0 && (
        <nav className={`${compact ? "" : "sticky top-20"} z-30 -mx-4 px-4 py-3 bg-cream/95 backdrop-blur border-b border-gold/20 flex gap-2 overflow-x-auto`} data-testid="public-menu-nav">
          {menu.map((b) => (
            <button key={b.category.id} onClick={() => go(b.category.id)}
              className="px-4 py-2 rounded-full text-sm whitespace-nowrap bg-white border border-gold/30 text-forest hover:bg-forest hover:text-white transition-colors"
              data-testid={`public-menu-nav-${b.category.id}`}>
              {loc(b.category, "name", lang)}
            </button>
          ))}
        </nav>
      )}
      <div className="space-y-16 mt-10">
        {menu?.map((block, i) => <CategoryBlock key={block.category.id} block={block} idx={i} lang={lang} />)}
        {menu && menu.length === 0 && <p className="text-muted-foreground">{tr(lang, "menu_updating")}</p>}
      </div>
    </div>
  );
};
